import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { selectIsEditMode } from 'features/common/editModeSlice';
import masterDataService from 'features/common/masterDataService';
import { MenuBookProperty } from 'features/common/models';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';
import { EatingWayDetailModal, EatingWaySettingModal, EatingWaySettingModalProps } from '../components';
import eatingWayService from '../eatingWayService';
import { EatingWayListResponse } from '../models/EatingWay';

function EatingWayListPage() {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // ref
  const selectedRowIndexRef = useRef<number[]>([]);
  const btnViewDetailRef = useRef<HTMLButtonElement>(null);
  const btnDeleteRef = useRef<HTMLButtonElement>(null);

  //State
  const [propertySource, setPropertySource] = useState<{ [key: number]: string }>();
  const [eatingWayList, setEatingWayList] = useState<Array<EatingWayListResponse>>([]);

  const [selectedEatingWayId, setSelectedEatingWayId] = useState<number>();
  const [showEatingWayDetailModal, setShowEatingWayDetailModal] = useState(false);
  const [eatingWaySettingMoldaData, setEatingWayDetailModalData] = useState<EatingWaySettingModalProps>();
  const [showEatingWaySettingModal, setShowEatingWaySettingModal] = useState(false);

  // status action buttons
  useEffect(() => {
    if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = true;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
  }, []);

  function loadPropertyDataSource() {
    //load data for property source
    (async () => {
      try {
        const response = await masterDataService.getMenuBookProperty();
        const propertyMap = response.data.menu_book_property.reduce(
          (menuBookPropertyMap: { [key: number]: string }, menuBookProperty: MenuBookProperty) => {
            menuBookPropertyMap[menuBookProperty.id] = menuBookProperty.name;
            return menuBookPropertyMap;
          },
          {}
        );
        setPropertySource(propertyMap);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  async function loadDataList() {
    try {
      const response = await eatingWayService.getAll({ displayType: 1 });
      setEatingWayList(response.data);
      handleUnSelectAll();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  useEffect(() => {
    loadPropertyDataSource();
    loadDataList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleOpenEatingWayDetailModal() {
    setSelectedEatingWayId(undefined);
    setShowEatingWayDetailModal(true);
  }

  function handleCloseEatingWayDetailModal(isDataChanged: boolean = false) {
    handleUnSelectAll();
    setSelectedEatingWayId(undefined);
    setShowEatingWayDetailModal(false);

    if (isDataChanged) {
      loadDataList();
    }
  }

  function handleShowDetail() {
    if (selectedRowIndexRef.current.length === 0 || selectedRowIndexRef.current.length > 1) {
      return;
    }

    const firstSelectedEatingWayId = selectedRowIndexRef.current[0];
    setSelectedEatingWayId(firstSelectedEatingWayId);
    setShowEatingWayDetailModal(true);
  }

  async function processDelete() {
    try {
      const response = await eatingWayService.delete({ ids: selectedRowIndexRef.current });
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      loadDataList();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function handleDelete() {
    if (selectedRowIndexRef.current.length === 0) {
      return;
    }

    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('EatingWayListPage.warn_confirm_delete')}
        onConfirm={processDelete}
      />
    ));
  }

  async function processRestore(id: number) {
    try {
      const response = await eatingWayService.restore({ ids: [id] });
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      loadDataList();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function handleRestore(id: number) {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('EatingWayListPage.warn_confirm_restore')}
        onConfirm={processRestore.bind(null, id)}
      />
    ));
  }

  function handleUnSelectAll() {
    const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('input[name="selected_eatingway"]:checked');
    chkSelectionElems.forEach(function (elem) {
      elem.checked = false;
    });

    selectedRowIndexRef.current = [];

    if (btnViewDetailRef.current) btnViewDetailRef.current.disabled = true;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
  }

  function handleSelectEatingWay() {
    const elements = document.querySelectorAll<HTMLInputElement>('input[name="selected_eatingway"]:checked');

    const selectedRowIndexs: number[] = [];
    elements.forEach(function (element) {
      selectedRowIndexs.push(Number(element.value));
    });
    selectedRowIndexRef.current = selectedRowIndexs;

    if (btnViewDetailRef.current && btnDeleteRef.current) {
      if (elements.length === 0) {
        btnViewDetailRef.current.disabled = true;
        btnDeleteRef.current.disabled = true;
      } else if (elements.length === 1) {
        btnViewDetailRef.current.disabled = false;
        btnDeleteRef.current.disabled = false;
      } else {
        btnViewDetailRef.current.disabled = true;
        btnDeleteRef.current.disabled = false;
      }
    }
  }

  function handleOpenEatingWaySettingModal(eatingWayItem: EatingWayListResponse) {
    setEatingWayDetailModalData({
      eatingWayId: eatingWayItem.id,
      saleTypeId: Number(eatingWayItem.sale_type?.id),
      saleTypeName: eatingWayItem.sale_type?.name,
      eatingWayName: eatingWayItem.name,
      propertyName: propertySource?.[Number(eatingWayItem?.property)] || '',
      eatingWayProperty: Number(eatingWayItem?.property),
    });
    setShowEatingWaySettingModal(true);
  }

  function handleCloseEatingWaySettingModal(isDataChanged: boolean) {
    setEatingWayDetailModalData(undefined);
    setShowEatingWaySettingModal(false);

    if (isDataChanged) {
      loadDataList();
    }
  }

  // compose isLoading status
  let isLoading = true;
  if (propertySource && eatingWayList) {
    isLoading = false;
  }

  if (isLoading) {
    return null;
  }

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <Row className="mb-3">
            <Col sm={6}>
              <FeatureTitleBar title={t('EatingWayListPage.head_title')}></FeatureTitleBar>
            </Col>
            <Col sm={6} className="text-end">
              <Button className="btn--dark-green" onClick={handleOpenEatingWayDetailModal}>
                {t('EatingWayListPage.add_new')}
              </Button>
            </Col>
          </Row>
          {/* actions */}
          <div className="text-end mb-2">
            <Button ref={btnViewDetailRef} className="ms-3" onClick={handleShowDetail}>
              {t('SaleTypeListPage.detailed')}
            </Button>
            <Button ref={btnDeleteRef} className="btn--red ms-3" onClick={handleDelete}>
              {t('SaleTypeListPage.delete')}
            </Button>
          </div>
          {/* list data */}
          <div className="sticky-table flex-grow-1 flex-shrink-1">
            <Table hover>
              <thead>
                <tr>
                  <th style={{ width: '5%' }}>{t('EatingWayListPage.selection')}</th>
                  <th style={{ width: '10%' }}>{t('EatingWayListPage.sale_type_name')}</th>
                  <th style={{ width: '15%' }}>{t('EatingWayListPage.eating_way_name')}</th>
                  <th style={{ width: '20%' }}>{t('EatingWayListPage.note')}</th>
                  <th style={{ width: '50%' }}>{t('EatingWayListPage.menu_book_selection')}</th>
                </tr>
              </thead>
              <tbody>
                {eatingWayList.map(function (eatingWayItem) {
                  const selectedMenuBookCourse: string[] = eatingWayItem.eating_way_settings.map(function (
                    eatingWaySetting
                  ) {
                    if (eatingWaySetting.course) {
                      return eatingWaySetting.menu_book.name + ' ' + eatingWaySetting.course.name;
                    }

                    return eatingWaySetting.menu_book.name;
                  });
                  return (
                    <tr key={eatingWayItem.id} className={`${eatingWayItem.delete_flg ? 'bg-light' : ''}`}>
                      <td className="text-center">
                        {eatingWayItem.delete_flg && (
                          <Button className="btn--orange" onClick={handleRestore.bind(null, eatingWayItem.id)}>
                            {t('EatingWayListPage.restore')}
                          </Button>
                        )}
                        {!eatingWayItem.delete_flg && (
                          <Form.Check
                            name="selected_eatingway"
                            className="custom-checkbox"
                            value={eatingWayItem.id}
                            onChange={handleSelectEatingWay}
                          ></Form.Check>
                        )}
                      </td>
                      <td className="text-center">{eatingWayItem.sale_type?.name}</td>
                      <td className="text-center">{eatingWayItem.name}</td>
                      <td>{eatingWayItem.note}</td>
                      <td className="wrap">
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="d-inline-block pe-5">{selectedMenuBookCourse.join(', ')}</span>
                          {!eatingWayItem.delete_flg && (
                            <Button
                              className="flex-grow-0 flex-shrink-0"
                              dangerouslySetInnerHTML={{ __html: t('EatingWayListPage.menu_book_edit') }}
                              onClick={handleOpenEatingWaySettingModal.bind(null, eatingWayItem)}
                            ></Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {showEatingWayDetailModal &&
        createPortal(
          <EatingWayDetailModal eatingWayId={selectedEatingWayId} onClose={handleCloseEatingWayDetailModal} />,
          document.body
        )}

      {showEatingWaySettingModal &&
        eatingWaySettingMoldaData &&
        createPortal(
          <EatingWaySettingModal
            eatingWayId={eatingWaySettingMoldaData.eatingWayId}
            saleTypeId={eatingWaySettingMoldaData.saleTypeId}
            saleTypeName={eatingWaySettingMoldaData.saleTypeName}
            eatingWayName={eatingWaySettingMoldaData.eatingWayName}
            propertyName={eatingWaySettingMoldaData.propertyName}
            eatingWayProperty={eatingWaySettingMoldaData.eatingWayProperty}
            onClose={handleCloseEatingWaySettingModal}
          />,
          document.body
        )}
    </div>
  );
}

export default EatingWayListPage;
